import type { FlashMessages, SharedData } from '@/types';
import { router, usePage } from '@inertiajs/react';
import { useEffect } from 'react';
import { toast } from 'sonner';

type FlashLevel = keyof FlashMessages;

const LEVELS: FlashLevel[] = ['success', 'error', 'warning', 'info'];

// Module-level guard so a partial reload (`router.reload({ only: [...] })`)
// that doesn't request `flash` — Inertia preserves the previous, lazy prop
// object by reference in that case — never re-toasts the same flash on the
// 'success' event it still emits. A full visit always builds a fresh `flash`
// object (even carrying the same message), so its distinct identity toasts
// as expected.
let lastFiredFlash: FlashMessages | undefined | null = null;

function fireFlash(flash: FlashMessages | undefined | null) {
    if (!flash) return;
    if (flash === lastFiredFlash) return;
    lastFiredFlash = flash;
    LEVELS.forEach((level) => {
        const message = flash[level];
        if (!message) return;
        toast[level](message);
    });
}

// Module-level guard so React StrictMode's double-invocation of effects in dev
// does not toast the initial flash twice. Inertia mounts FlashToastBridge once
// per SPA lifetime, so this never blocks real subsequent fires.
let initialFlashFired = false;

export function useFlashToast() {
    const { props } = usePage<SharedData>();

    useEffect(() => {
        if (!initialFlashFired) {
            initialFlashFired = true;
            fireFlash(props.flash);
        }

        // Inertia emits 'success' on every completed visit (including those
        // that return validation errors as props). History Back/Forward
        // restores props from history.state and does NOT emit 'success',
        // which is exactly the behavior we want — no stale toast replays.
        const unsubscribe = router.on('success', (event) => {
            const page = event.detail.page as unknown as { props?: SharedData } | undefined;
            fireFlash(page?.props?.flash);
        });

        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
